import {
  buildUrl,
  CS_WITHDRAW_APPLICATION_ENDPOINT,
  JC_API_URL,
  NHE_APPOINTMENT_ENDPOINT,
  SDA_API_URL,
  APPLICATION_MANAGE_DATA_API_URL,
  DOCUMENT_STATUS_ENDPOINT,
  APPLICATION_MANAGE_SF_DATA_API_URL,
  ATOZ_ENDPOINT,
  SALESFORCE_JOB_DETAILS_URL,
} from "../config/urls";
import { fetcher } from "../helpers/fetcher";
import { sampleHtml } from "../components/sda/sample";
import * as Cookie from "js-cookie";

export interface SdaInfo {
  errorCode?: number;
  startDate: string;
  startTime: string;
  publicText: string;
  address: string;
  startDateStatus: string;
  timezone: string;
}

export interface Error {
  errorStatusCode: string;
  errorMessage: string;
  errorCode: string;
}

export interface JobDetailsModel {
  error?: Error;
  scheduleId?: string;
  geoClusterId?: string;
  externalJobTitle: string;
  scheduleBannerText?: string;
  scheduleText?: string;
  firstDayOnSite?: string;
  startTime?: string;
  briefJobDescription: string;
  basePay?: number;
  address: string;
  city: string;
  state: string;
  postalCode: string;
  image: string;
  shiftDifferential?: number;
  signOnBonus?: number;
  surgePay?: number;
  hoursPerWeek?: number;
  businessLine?: string;
  totalPayRate?: number;
  hoursPerWeekL10N: string;
  firstDayOnSiteL10N: string;
  startTimeL10N: string;
  basePayL10N?: string;
  signOnBonusL10N?: string;
  surgePayL10N?: string;
  totalPayRateL10N: string;
  requiredLanguage: Array<string>;
  requiredLanguageFromSalesForce?: string;
  employmentType: string;
  employmentTypeL10N?: string;
  parsedTrainingDate?: string;
  monthlyBasePay?: number;
  monthlyBasePayL10N?: string;
  currencyCode?: string;
  siteId?: string;
  countryGroup: string;
  jobId: string;
  requisitionCategory?: string;
  isScheduleReleased?: boolean;
  startDateStatus?: string;
  publicText?: string;
  timezone?: string;
  fullAddress?: string;
  jobContainerJobMeta?: string;
  isShiftReleased?: boolean;
}
export interface ApplicationManageData {
  enableCSS: boolean;
  hideWithdrawApplication: boolean;
  applicationState: string | null;
  applicationStepSubStep?: string;
}

export interface WithdrawApplicationResponse {
  statusCode: number;
  errorMessage: string;
}

export interface NheStatus {
  status: string;
}

export interface NheDetails {
  errorStatusCode?: number;
  errorMessage?: string;
  errorCode?: string;
  nheDate: string;
  nheTimeRange: string;
  address: string;
  timezone: string;
  inPast: boolean;
  isStartTimeInPast: boolean;
  status: string;
  supportedLanguage?: string[];
  entrance?: string;
  parking?: string;
  access?: string;
}

export interface DocumentStatus {
  results: [
    {
      error: string;
      candidateId: string;
      documentsAvailable: boolean;
      documentCompletionStatus: string;
      onbaseLoginUrl: string;
    }
  ];
  errorCode: string;
  errorMessage: string;
}

const userDefinedLocale = Cookie.get("hvh-locale");
const DEFAULT_LOCALE = "en-US";
const locale = userDefinedLocale && userDefinedLocale.length > 0 ? userDefinedLocale : DEFAULT_LOCALE;

export interface AtoZStatus {
  aToZLoginUrl: string;
  employeeLogin: string;
  employeeId: string;
  personId: string;
  isPasswordSet: boolean;
  isAccountReady: boolean;
  errorCode: number;
  errorMessage: string;
  siteName: string;
  day1Date: string;
}

const cachedRequestsFromSDA: Record<string, Promise<SdaInfo>> = {};
const cachedRequestsFromJC: Record<string, Promise<JobDetailsModel>> = {};
const cachedRequestsFromSalesforce: Record<string, Promise<JobDetailsModel>> = {};
const cachedNHERequestsFromNHE: Record<string, Promise<NheDetails>> = {};
const cachedApplicationManagement: Record<string, Promise<ApplicationManageData>> = {};

export const getStartDateInfo = (applicationId?: string): Promise<SdaInfo> => {
  if (process.env.NODE_ENV === "development") {
    if (process.env.REACT_APP_USE_MOCK_REQUESTS === "true") {
      console.warn("getStartDateInfo", "mocked request", applicationId);
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve({
            startDate: "Wed Dec 30",
            startTime: "7:30AM",
            publicText: sampleHtml,
            address: "SAV3-7001 Skipper Road Macon, GA 31216",
            startDateStatus: "TENTATIVE",
            timezone: "America/New_York",
          });
        }, 1000);
      });
    }
  }
  if (!applicationId) {
    throw new Error("applicationId should be provided");
  }
  if (!cachedRequestsFromSDA[applicationId]) {
    const url = buildUrl(SDA_API_URL, {
      applicationId,
      locale,
    });
    cachedRequestsFromSDA[applicationId] = fetcher(url);
  }
  return cachedRequestsFromSDA[applicationId];
};

export const getJobScheduleDetails = async (applicationId?: string): Promise<JobDetailsModel> => {
  if (process.env.NODE_ENV === "development") {
    if (process.env.REACT_APP_USE_MOCK_REQUESTS === "true") {
      console.warn("getJobScheduleDetails", "mocked request", applicationId);
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve({
            externalJobTitle: "Amazon Fulfillment Center Warehouse Associate",
            scheduleText:
              "Week1: Sun, Mon, Tue, Wed 02:30am - 02:00pm\nWeek2: Sun, Mon, Tue 02:30am - 02:00pm\nWeek3: Fri, Sat, Sun 02:30am - 02:00pm\nWeek4: Tue, Wed 02:30am - 02:00pm",
            firstDayOnSite: "Wed Sep 22",
            startTime: "11:00",
            briefJobDescription:
              "You'll be part of the team that gets orders ready for customers relying on Amazon's services.",
            basePay: 16.4,
            address: "7200 Dorsey Run Rd",
            city: "Elkridge",
            state: "MD",
            postalCode: "21075",
            image:
              "https://m.media-amazon.com/images/G/01/HVHJobDetails/I_Fulfillment_Center_Associate._CB1198675309_.svg",
            shiftDifferential: 3,
            signOnBonus: 2000,
            scheduleBannerText: "Up to $5/hr between 12AM to 6AM",
            hoursPerWeek: 40,
            totalPayRate: 19.4,
            surgePay: 2,
            hoursPerWeekL10N: "40",
            firstDayOnSiteL10N: "2022-09-22",
            startTimeL10N: "11:00",
            basePayL10N: "$16.40",
            signOnBonusL10N: "$2000.00",
            surgePayL10N: "$2.00",
            totalPayRateL10N: "$19.40",
            geoClusterId: "geoClusterId",
            jobTitle: "jobTitle",
            scheduleId: "scheduleId",
            businessLine: "GSF",
            requiredLanguage: ["en-US", "es-US"],
            employmentType: "Regular",
            employmentTypeL10N: "Regular",
            parsedTrainingDate: "2022-07-01 3:00 a. m. - 7:30 a. m.\n2022-07-02 3:00 a. m. - 07:30 a. m.",
            monthlyBasePay: 6000.0,
            monthlyBasePayL10N: "$6000.00",
            currencyCode: "MXN",
            siteId: "SITE-10",
            countryGroup: "US",
            jobId: "jobId1",
            requisitionCategory: "Customer Service",
            isScheduleReleased: false,
            isShiftReleased: true,
          } as JobDetailsModel);
        }, 1000);
      });
    }
  }
  if (!applicationId) {
    throw new Error("applicationId should be provided");
  }

  if (!cachedRequestsFromJC[applicationId]) {
    const url = buildUrl(JC_API_URL, {
      applicationId,
      locale,
    });
    cachedRequestsFromJC[applicationId] = fetcher(url);
  }

  return cachedRequestsFromJC[applicationId];
};

export const getSalesforceJobDetails = async (applicationId?: string): Promise<JobDetailsModel> => {
  if (process.env.NODE_ENV === "development") {
    if (process.env.REACT_APP_USE_MOCK_REQUESTS === "true") {
      console.warn("getSalesforceJobDetails", "mocked request", applicationId);
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve({
            externalJobTitle: "Amazon Fulfillment Center Warehouse Associate",
            scheduleText:
              "Week1: Sun, Mon, Tue, Wed 02:30am - 02:00pm\nWeek2: Sun, Mon, Tue 02:30am - 02:00pm\nWeek3: Fri, Sat, Sun 02:30am - 02:00pm\nWeek4: Tue, Wed 02:30am - 02:00pm",
            firstDayOnSite: "Wed Sep 22",
            startTime: "11:00",
            briefJobDescription:
              "You'll be part of the team that gets orders ready for customers relying on Amazon's services.",
            basePay: 16.4,
            address: "7200 Dorsey Run Rd",
            city: "Elkridge",
            state: "MD",
            postalCode: "21075",
            image:
              "https://m.media-amazon.com/images/G/01/HVHJobDetails/I_Fulfillment_Center_Associate._CB1198675309_.svg",
            shiftDifferential: 3,
            signOnBonus: 2000,
            scheduleBannerText: "Up to $5/hr between 12AM to 6AM",
            hoursPerWeek: 40,
            totalPayRate: 19.4,
            surgePay: 2,
            hoursPerWeekL10N: "40",
            firstDayOnSiteL10N: "2022-09-22",
            startTimeL10N: "11:00",
            basePayL10N: "$16.40",
            signOnBonusL10N: "$2000.00",
            surgePayL10N: "$2.00",
            totalPayRateL10N: "$19.40",
            geoClusterId: "geoClusterId",
            jobTitle: "jobTitle",
            scheduleId: "scheduleId",
            businessLine: "GSF",
            requiredLanguage: ["en-US", "es-US"],
            employmentType: "Regular",
            employmentTypeL10N: "Regular",
            parsedTrainingDate: "2022-07-01 3:00 a. m. - 7:30 a. m.\n2022-07-02 3:00 a. m. - 07:30 a. m.",
            monthlyBasePay: 6000.0,
            monthlyBasePayL10N: "$6000.00",
            currencyCode: "MXN",
            siteId: "SITE-10",
            countryGroup: "US",
            jobId: "jobId1",
            requisitionCategory: "Customer Service",
          } as JobDetailsModel);
        }, 1000);
      });
    }
  }
  if (!applicationId) {
    throw new Error("applicationId should be provided");
  }

  if (!cachedRequestsFromSalesforce[applicationId]) {
    const url = buildUrl(SALESFORCE_JOB_DETAILS_URL, {
      applicationId,
      locale,
    });
    cachedRequestsFromSalesforce[applicationId] = fetcher(url);
  }
  return cachedRequestsFromSalesforce[applicationId];
};
export const getApplicationManageData = (applicationId?: string): Promise<ApplicationManageData> => {
  if (process.env.NODE_ENV === "development") {
    if (process.env.REACT_APP_USE_MOCK_REQUESTS === "true") {
      console.warn("getApplicationManageData", "mocked request", applicationId);
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve({
            enableCSS: true,
            hideWithdrawApplication: true,
            applicationState: "READY_TO_HIRE",
          } as ApplicationManageData);
        }, 1000);
      });
    }
  }
  if (!applicationId) {
    throw new Error("applicationId should be provided");
  }
  if (!cachedApplicationManagement[applicationId]) {
    const url = buildUrl(APPLICATION_MANAGE_DATA_API_URL, {
      applicationId,
      locale,
    });
    cachedApplicationManagement[applicationId] = fetcher(url);
  }
  return cachedApplicationManagement[applicationId];
};

export const getApplicationManageDataFromSalesforce = (applicationId?: string): Promise<ApplicationManageData> => {
  if (process.env.NODE_ENV === "development") {
    if (process.env.REACT_APP_USE_MOCK_REQUESTS === "true") {
      console.warn("getApplicationManageDataFromSalesforce", "mocked request", applicationId);
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve({
            enableCSS: true,
            hideWithdrawApplication: true,
            applicationState: "READY_TO_HIRE",
          } as ApplicationManageData);
        }, 1000);
      });
    }
  }
  if (!applicationId) {
    throw new Error("applicationId should be provided");
  }
  if (!cachedApplicationManagement[applicationId]) {
    const url = buildUrl(APPLICATION_MANAGE_SF_DATA_API_URL, {
      applicationId,
    });
    cachedApplicationManagement[applicationId] = fetcher(url);
  }
  return cachedApplicationManagement[applicationId];
};

export const getNHEAppointment = (applicationId?: string): Promise<NheDetails> => {
  if (process.env.NODE_ENV === "development") {
    if (process.env.REACT_APP_USE_MOCK_REQUESTS === "true") {
      console.warn("getNHEAppointment", "mocked request", applicationId);
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve({
            nheDate: "Jan 14, 2025",
            nheTimeRange: "11:30PM - 12:30AM",
            address: "224 43th Street, New York, NY 98198",
            timezone: "America/New_York",
            inPast: false,
            isStartTimeInPast: false,
            status: "COMPLETED",
            supportedLanguage: ["en-US", "es-US"],
            entrance: "Site B123, Gate 1, Door 2",
            parking: "available in lot",
            access: "Wheel chair accessible",
          });
        }, 1000);
      });
    }
  }
  if (!applicationId) {
    throw new Error("applicationId should be provided");
  }
  if (!cachedNHERequestsFromNHE[applicationId]) {
    const url = buildUrl(NHE_APPOINTMENT_ENDPOINT, {
      applicationId,
    });
    cachedNHERequestsFromNHE[applicationId] = fetcher(url);
  }
  return cachedNHERequestsFromNHE[applicationId];
};

export const withdrawApplication = (
  applicationId?: string,
  withDrawReason?: string
): Promise<WithdrawApplicationResponse> => {
  if (!applicationId) {
    throw new Error("applicationId should be provided");
  }
  const url = buildUrl(CS_WITHDRAW_APPLICATION_ENDPOINT, {
    applicationId,
  });
  return fetcher(url, {
    method: "POST",
    body: JSON.stringify({ withDrawReason }),
  });
};

export const getDocumentStatusInfo = (
  href?: string,
  candidateGlobalId?: string,
  applicationId?: string,
  locale?: string
): Promise<DocumentStatus> => {
  if (!applicationId) {
    throw new Error("applicationId should be provided");
  }

  const url = buildUrl(DOCUMENT_STATUS_ENDPOINT, {
    candidateGlobalId,
    applicationId,
    locale,
  });

  return fetcher(url);
};

export const getCandidateAtoZStatus = (
  href?: string,
  candidateGlobalId?: string,
  applicationId?: string,
  locale?: string,
  returnResetPasswordUrl?: string
): Promise<AtoZStatus> => {
  if (!candidateGlobalId) {
    throw new Error("candidateGlobalId should be provided");
  }

  let url = buildUrl(ATOZ_ENDPOINT, {
    candidateGlobalId,
    applicationId,
    locale,
  });
  url += "/" + returnResetPasswordUrl;
  return fetcher(url);
};
